import type { LegacyBlog } from "../api/blog.api";

/**
 * Known Moroccan city tokens used to cluster related articles by place.
 * Each group is [canonical, ...spelling variants], all lowercase, so
 * "marrakesh"/"marrakech" and "fes"/"fez" resolve to the same city.
 */
const CITY_TOKENS: readonly (readonly string[])[] = [
  ["marrakech", "marrakesh"],
  ["casablanca", "casa"],
  ["fez", "fes"],
  ["rabat"],
  ["essaouira"],
  ["agadir"],
  ["tangier", "tanger"],
  ["chefchaouen"],
  ["meknes", "meknès"],
  ["ouarzazate"],
  ["dakhla"],
  ["merzouga"],
  ["agafay"],
  ["taghazout"],
  ["tetouan", "tétouan"],
  ["ifrane"],
  ["asilah"],
];

/** First city whose name (or a variant) appears in `text`, else null. */
function detectCity(text: string): string | null {
  const s = text.toLowerCase();
  for (const group of CITY_TOKENS) {
    if (group.some((tok) => s.includes(tok))) return group[0];
  }
  return null;
}

type CurrentInput =
  | string
  | { slug: string; title?: string; hashtags?: string[] };

/**
 * Related posts for an article, ranked by topical relevance: same city (+3)
 * and shared hashtags (+1 each), tie-broken by the incoming (recency) order.
 * Falls back to plain recency when there is no signal, so passing a bare slug
 * reproduces the old "recent minus current" behaviour. Pure + unit-tested; kept
 * out of `blog.api.ts` so consumers that mock the API module aren't coupled to
 * this helper.
 */
export function buildRelatedPosts(
  posts: LegacyBlog[],
  current: CurrentInput,
  limit = 3,
) {
  const currentSlug = typeof current === "string" ? current : current.slug;
  const currentTitle = typeof current === "string" ? "" : current.title ?? "";
  const currentHashtags =
    typeof current === "string"
      ? []
      : (current.hashtags ?? []).map((h) => h.toLowerCase());
  const currentCity = detectCity(`${currentSlug} ${currentTitle}`);

  const scored = posts
    .filter((p) => p.slug !== currentSlug)
    .map((p, index) => {
      let score = 0;
      const city = detectCity(`${p.slug} ${p.title}`);
      if (currentCity && city === currentCity) score += 3;
      if (currentHashtags.length > 0 && p.hashtags && p.hashtags.length > 0) {
        const tags = p.hashtags.map((h) => h.toLowerCase());
        score += tags.filter((t) => currentHashtags.includes(t)).length;
      }
      return { p, index, score };
    });

  // Higher score first; ties keep the original (recency) order.
  scored.sort((a, b) => b.score - a.score || a.index - b.index);

  return scored.slice(0, limit).map(({ p }) => ({
    id: p.id,
    slug: p.slug,
    title: p.title,
    excerpt: p.excerpt ?? "",
    image: p.image,
    date: p.date,
    category: p.category,
    author: p.author,
  }));
}
